import type { ClientGame, ClientGameCtx } from "./index";
import { SNK } from "../../shared/arcade";
import { ArcadeControls } from "../controls";
import { sound } from "../sound";
import { Fx } from "../fx";
import { puck, roundRectPath } from "../draw";
import { countdownOverlay } from "./asteroids";
import { C, TINT, hudPill } from "../theme";

const W = SNK.W;
const H = SNK.H;
const MINE = C.green;
const MINE_DARK = "#1FA85A";
const RIVAL = C.coral;
const RIVAL_DARK = "#D9473A";
const DEAD = C.faint;
const DEAD_DARK = C.muted;
const TILE_ALT = "#E6F8EF"; // the lighter checker square on the grass board

// ---- shared sticker-HUD helpers (also used by tetris / bubble) ----

let safeTopCache = -1;
/**
 * env(safe-area-inset-top) in CSS px. The game canvas runs edge to edge under
 * the status bar, but the quit button and the solo best-score banner are
 * positioned below the inset, so HUD rows must be too. Measured once.
 */
export function safeTop(): number {
  if (safeTopCache >= 0) return safeTopCache;
  try {
    const p = document.createElement("div");
    p.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;padding-top:env(safe-area-inset-top)";
    document.body.appendChild(p);
    safeTopCache = parseFloat(getComputedStyle(p).paddingTop) || 0;
    p.remove();
  } catch { safeTopCache = 0; }
  return safeTopCache;
}

export const HUD_PILL = 16;
export const HUD_PILL_H = Math.round(HUD_PILL * 1.9);

/**
 * Y of the top HUD row. The quit button owns the top-left ~50px; in solo runs
 * the best-score banner sits centred at top:30px, so the row drops below it.
 */
export function hudRowY(solo: boolean): number {
  return safeTop() + (solo ? 60 : 8);
}

/** a row of white sticker pills, right-aligned to `right` (laid out right to left) */
export function pillRowRight(
  g: CanvasRenderingContext2D,
  right: number, y: number,
  items: { text: string; label?: string; ink?: string }[],
) {
  let x = right;
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i]!;
    x -= hudPill(g, x, y, it.text, { align: "right", size: HUD_PILL, label: it.label, ink: it.ink }) + 8;
  }
}

/** the 3px ink-outlined rounded board with a hard ink drop shadow */
export function boardPanel(
  g: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
  fill: string | CanvasGradient,
) {
  roundRectPath(g, x + 2, y + 5, w, h, r);
  g.fillStyle = C.ink;
  g.fill();
  roundRectPath(g, x, y, w, h, r);
  g.fillStyle = fill;
  g.fill();
}
export function boardOutline(
  g: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  roundRectPath(g, x, y, w, h, r);
  g.strokeStyle = C.ink;
  g.lineWidth = 3;
  g.lineJoin = "round";
  g.stroke();
}

export class SnakeClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private controls = new ArcadeControls();
  private fx = new Fx();
  private raf = 0;
  private last = performance.now();
  private t = 0;
  private wantDir = -1;
  private sentDir = -1;
  private pollTimer = 0;
  private layout = { ox: 0, oy: 0, cell: 10 };

  private get st() { return this.ctx.room.state.snake; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);
    this.controls.mount(ctx.root, { hint: "swipe / drag to steer", radius: 70, deadzone: 0.35, curve: 1 });

    ctx.room.onMessage("s-eat", (m: any) => {
      if (m?.slot === ctx.mySlot) sound.snakeEat();
      const head = this.headCell(m?.slot ?? 0);
      if (head) this.fx.burst(head.x, head.y, { count: 9, speed: 0.07, colour: [C.mango, TINT.mango, C.white], life: 380, gravity: 0.0002 });
    });
    ctx.room.onMessage("s-crash", (m: any) => {
      const head = this.headCell(m?.slot ?? 0);
      if (head) this.fx.burst(head.x, head.y, { count: 20, speed: 0.14, colour: m?.slot === ctx.mySlot ? [MINE, TINT.mint, C.ink] : [RIVAL, TINT.coral, C.ink], life: 550, square: true, gravity: 0.0003 });
      if (m?.slot === ctx.mySlot) {
        sound.shipBoom();
        this.fx.shake(9, 380);
        try { navigator.vibrate?.(40); } catch { /* */ }
      }
    });

    this.pollTimer = window.setInterval(() => this.pollDir(), 60);
    this.last = performance.now();
    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    clearInterval(this.pollTimer);
    this.controls.unmount();
  }

  private headCell(slot: number): { x: number; y: number } | null {
    const side = this.st.sides?.[slot];
    const i = side?.body ? Number(String(side.body).split(",")[0]) : NaN;
    if (!Number.isFinite(i)) return null;
    const { ox, oy, cell } = this.layout;
    return { x: ox + (i % W) * cell + cell / 2, y: oy + Math.floor(i / W) * cell + cell / 2 };
  }

  private pollDir() {
    if (this.ctx.room.state.phase !== "playing") return;
    const x = this.controls.x, y = this.controls.y;
    const mag = Math.hypot(x, y);
    if (mag < 0.4) return;
    const dir = Math.abs(x) > Math.abs(y) ? (x > 0 ? 0 : 2) : (y > 0 ? 1 : 3);
    this.wantDir = dir;
    if (dir !== this.sentDir) {
      this.sentDir = dir;
      this.ctx.room.send("turn", { dir });
    }
  }

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this.t += dt;
    try { this.draw(dt); } catch (e) { console.error("snake frame", e); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw(dt: number) {
    const { room, mySlot } = this.ctx;
    const st = this.st;
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) { c.width = cw * dpr; c.height = ch * dpr; }
    g.save();
    g.scale(dpr, dpr);

    this.fx.update(dt);
    const sh = this.fx.shakeXY();

    // garden scene, edge to edge
    g.fillStyle = TINT.mint;
    g.fillRect(0, 0, cw, ch);

    const solo = !!st.solo || !st.sides || st.sides.length < 2;
    const hudY = hudRowY(solo);
    const top = hudY + HUD_PILL_H + 14;
    const pad = 12;
    // bottom reserve: room for the thumb under the board (the joystick floats
    // wherever the finger lands, so this is comfort, not a hard control area)
    const bottom = 84;
    const cell = Math.max(6, Math.min((cw - pad * 2) / W, (ch - top - bottom) / H));
    const bw = cell * W, bh = cell * H;
    const ox = (cw - bw) / 2;
    const oy = top + Math.max(0, (ch - top - bottom - bh) / 2);
    this.layout = { ox, oy, cell };

    g.save();
    g.translate(sh.x, sh.y);

    // board — a white/pale-green checker lawn in an ink sticker frame
    const br = Math.max(8, cell * 0.6);
    boardPanel(g, ox, oy, bw, bh, br, C.white);
    g.save();
    roundRectPath(g, ox, oy, bw, bh, br);
    g.clip();
    g.fillStyle = TILE_ALT;
    for (let y = 0; y < H; y++) {
      for (let x = (y & 1); x < W; x += 2) g.fillRect(ox + x * cell, oy + y * cell, cell, cell);
    }
    g.restore();
    boardOutline(g, ox, oy, bw, bh, br);

    const cx = (i: number) => ox + (i % W) * cell + cell / 2;
    const cy = (i: number) => oy + Math.floor(i / W) * cell + cell / 2;

    // food — a pulsing mango apple with a leaf
    const pulse = 1 + Math.sin(this.t * 0.006) * 0.08;
    (st.food ? String(st.food).split(",") : []).forEach((s: string) => {
      const i = Number(s);
      if (!Number.isFinite(i)) return;
      g.strokeStyle = C.ink; g.lineWidth = Math.max(1.6, cell * 0.12); g.lineCap = "round";
      g.beginPath(); g.moveTo(cx(i), cy(i) - cell * 0.3); g.lineTo(cx(i) + cell * 0.16, cy(i) - cell * 0.5); g.stroke();
      puck(g, cx(i), cy(i), cell * 0.36 * pulse, C.mango);
      g.fillStyle = C.mint;
      g.beginPath(); g.ellipse(cx(i) + cell * 0.2, cy(i) - cell * 0.44, cell * 0.14, cell * 0.07, -0.5, 0, Math.PI * 2); g.fill();
      g.strokeStyle = C.ink; g.lineWidth = Math.max(1, cell * 0.06); g.stroke();
    });

    // snakes — hard ink shadows first, then the bodies, so a shadow never
    // lands on top of a neighbouring segment
    const me = mySlot === 1 ? 1 : 0;
    const segs = (side: any) => (side.body ? String(side.body).split(",").map(Number) : []) as number[];
    const segRect = (i: number, k: number) => {
      const x = ox + (i % W) * cell, y = oy + Math.floor(i / W) * cell;
      const inset = k === 0 ? cell * 0.04 : cell * 0.1;
      return { x: x + inset, y: y + inset, w: cell - inset * 2 };
    };
    st.sides?.forEach((side: any) => {
      g.fillStyle = C.ink;
      segs(side).forEach((i, k) => {
        if (!Number.isFinite(i)) return;
        const r = segRect(i, k);
        roundRectPath(g, r.x + 1, r.y + 2.5, r.w, r.w, cell * 0.32);
        g.fill();
      });
    });
    st.sides?.forEach((side: any, slot: number) => {
      const col = side.alive ? (slot === me ? MINE : RIVAL) : DEAD;
      const dark = side.alive ? (slot === me ? MINE_DARK : RIVAL_DARK) : DEAD_DARK;
      const cells = segs(side);
      // draw tail first so the head ends up on top
      for (let k = cells.length - 1; k >= 0; k--) {
        const i = cells[k]!;
        if (!Number.isFinite(i)) continue;
        const r = segRect(i, k);
        // body
        roundRectPath(g, r.x, r.y, r.w, r.w, cell * 0.32);
        g.fillStyle = k % 2 ? dark : col;
        if (k === 0) g.fillStyle = col;
        g.fill();
        g.strokeStyle = C.ink;
        g.lineWidth = Math.max(1.4, cell * 0.11);
        g.lineJoin = "round";
        g.stroke();
        // top-left sheen
        g.fillStyle = "rgba(255,255,255,0.3)";
        roundRectPath(g, r.x + r.w * 0.16, r.y + r.w * 0.14, r.w * 0.46, r.w * 0.26, cell * 0.14);
        g.fill();
        // head eyes
        if (k === 0 && side.alive) {
          const x = ox + (i % W) * cell, y = oy + Math.floor(i / W) * cell;
          const d = side.dir ?? 0;
          const ex = [0.22, 0, -0.22, 0][d]!, ey = [0, 0.22, 0, -0.22][d]!;
          const px = [0, -0.2, 0, 0.2][d]!, py = [-0.2, 0, 0.2, 0][d]!;
          const dx = [1, 0, -1, 0][d]!, dy = [0, 1, 0, -1][d]!;
          for (const s2 of [-1, 1]) {
            const exx = x + cell / 2 + (ex + px * s2) * cell, eyy = y + cell / 2 + (ey + py * s2) * cell;
            g.beginPath();
            g.arc(exx, eyy, Math.max(1.6, cell * 0.15), 0, Math.PI * 2);
            g.fillStyle = C.white;
            g.fill();
            g.strokeStyle = C.ink;
            g.lineWidth = Math.max(1, cell * 0.05);
            g.stroke();
            g.beginPath();
            g.arc(exx + dx * cell * 0.05, eyy + dy * cell * 0.05, Math.max(1, cell * 0.075), 0, Math.PI * 2);
            g.fillStyle = C.ink;
            g.fill();
          }
        }
      }
    });

    this.fx.draw(g);
    g.restore();

    // HUD — white sticker pills, top right (below the best banner in solo)
    const mine = st.sides?.[me];
    const rival = st.sides?.[me === 0 ? 1 : 0];
    if (solo) {
      pillRowRight(g, cw - pad, hudY, [
        { text: String(mine?.score ?? 0), label: "score" },
        { text: String(mine?.len ?? 0), label: "length" },
      ]);
    } else {
      pillRowRight(g, cw - pad, hudY, [
        { text: String(mine?.score ?? 0), label: "you", ink: MINE_DARK },
        { text: String(rival?.score ?? 0), label: "rival", ink: RIVAL_DARK },
        { text: String(SNK.WIN_LEN_2P), label: "first to" },
      ]);
    }

    if (room.state.phase === "countdown") countdownOverlay(g, cw, ch, room.state.countdown);
    g.restore();
  }

  results(ctx: ClientGameCtx): HTMLElement {
    const st = ctx.room.state.snake;
    const me = ctx.mySlot === 1 ? 1 : 0;
    const mine = st.sides?.[me];
    const d = document.createElement("div");
    d.className = "arc-final";
    if (st.solo) {
      d.innerHTML =
        `<div class="score-big">${mine?.score ?? 0}</div>` +
        `<div class="arc-note">final length ${mine?.len ?? 0}</div>`;
    } else {
      const rival = st.sides?.[me === 0 ? 1 : 0];
      d.innerHTML =
        `<div class="score-big">${mine?.score ?? 0} : ${rival?.score ?? 0}</div>` +
        `<div class="arc-note">length ${mine?.len ?? 0} vs ${rival?.len ?? 0}</div>`;
    }
    return d;
  }
}
